// ============================================
// ANTI-AGING QUESTIONNAIRE DATA
// ============================================
// Based on Beluga Health Combined Anti-Aging Questionnaire
// Medications: NAD (SC, IN, topical), MIC, Metformin, Vitamin B12, Glutathione
// Visit Type: antiAging
// Follow-Up Frequency: Every 6-12 months

// ============================================
// DISQUALIFYING CONDITIONS
// ============================================
export const ANTI_AGING_DISQUALIFYING_CONDITIONS = [
  'G6PD deficiency',
  'Chronic kidney disease (CKD)',
  'Liver cirrhosis',
  'Currently undergoing cancer treatment'
];

// Primary reasons for medication
export const primaryReasonOptions = [
  { label: 'General wellness', value: 'general-wellness' },
  { label: 'Anti-Aging', value: 'anti-aging' },
  { label: 'Antioxidant benefits', value: 'antioxidant-benefits' },
  { label: 'Boost energy', value: 'boost-energy' },
  { label: 'Brighten skin', value: 'brighten-skin' },
  { label: 'Detoxification', value: 'detoxification' },
  { label: 'Immune support', value: 'immune-support' },
  { label: 'Other', value: 'other' }
];

// Current symptoms options
export const currentSymptomsOptions = [
  { label: 'Fatigue or Low Energy', value: 'fatigue' },
  { label: 'Memory Issues', value: 'memory-issues' },
  { label: 'Difficulty Concentrating', value: 'difficulty-concentrating' },
  { label: 'Depression or Anxiety', value: 'depression-anxiety' },
  { label: 'Sleep Disorders', value: 'sleep-disorders' },
  { label: 'Chronic Pain', value: 'chronic-pain' },
  { label: 'Other', value: 'other' },
  { label: 'None of these apply to me', value: 'none' }
];

// Medical history conditions (disqualifying)
export const medicalHistoryOptions = [
  { label: 'G6PD deficiency (glucose-6-phosphate dehydrogenase deficiency)', value: 'g6pd', disqualifies: true },
  { label: 'Chronic kidney disease (CKD)', value: 'ckd', disqualifies: true },
  { label: 'Liver cirrhosis', value: 'liver-cirrhosis', disqualifies: true },
  { label: 'None of the above', value: 'none' }
];

// ============================================
// ANTI-AGING QUESTIONS
// ============================================

const sexAtBirthQuestion = {
  id: 'sex-at-birth',
  type: 'single-select',
  question: 'What was your sex assigned at birth?',
  options: [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' }
  ]
};

const pregnancyStatusQuestion = {
  id: 'pregnancy-status',
  type: 'single-select',
  question: 'Are you currently pregnant or breastfeeding?',
  options: [
    { label: 'Yes', value: 'yes', disqualifies: true },
    { label: 'No', value: 'no' }
  ]
};

const pregnancyConsentQuestion = {
  id: 'pregnancy-consent',
  type: 'consent-dropdown',
  question: 'Consent (Pregnancy)',
  consentText: `I understand that the medication prescribed to me by my healthcare provider may not be safe to take during pregnancy or while breastfeeding. Most of the products offered in this treatment encounter are not recommended when attempting to conceive, and taking them during pregnancy or breastfeeding could pose risks to my health and the health of a developing fetus or baby. An exception is NAD, which may be used while trying to become pregnant.

I agree to take necessary precautions to avoid becoming pregnant while using any other medications from this encounter, including the use of effective contraception methods as discussed with my healthcare provider.

I understand that I should stop taking these medications before attempting to become pregnant and consult with my healthcare provider prior to discontinuation and when planning a pregnancy, to ensure my safety and well-being.`,
  options: [
    { label: 'I have read and understand the above information, I understand the risks and wish to proceed', value: 'consent' },
    { label: 'I have read the information and do NOT wish to proceed', value: 'decline', disqualifies: true }
  ]
};

const heightWeightQuestion = {
  id: 'height-weight',
  type: 'height-weight-bmi',
  question: 'What is your height and weight?',
  subtitle: 'This information helps us personalize your treatment',
  skipBmiCheck: true // No BMI disqualification for anti-aging
};

const medicalConditionsQuestion = {
  id: 'medical-conditions',
  type: 'textarea',
  question: 'Please identify all your current medical conditions',
  subtitle: 'Include chronic conditions, past surgeries, and any other relevant health issues',
  placeholder: 'e.g., Hypertension, Asthma, Previous knee surgery, etc...',
  required: true
};

const medicationsListQuestion = {
  id: 'medications-list',
  type: 'textarea',
  question: 'Please list all your current medications including dosages',
  subtitle: 'Include prescription medications, over-the-counter drugs, and supplements',
  placeholder: 'e.g., Metformin 500mg twice daily, Lisinopril 10mg daily, Vitamin D 2000 IU daily...',
  required: true
};

const allergiesQuestion = {
  id: 'allergies',
  type: 'textarea',
  question: 'Please list all of your known allergies',
  subtitle: 'Include drug allergies, food allergies, and environmental allergies',
  placeholder: 'e.g., Penicillin (causes rash), Shellfish (anaphylaxis), Seasonal allergies...',
  required: true
};

const primaryReasonQuestion = {
  id: 'primary-reason',
  type: 'multi-select',
  question: 'What is your primary reason for requesting this medication?',
  subtitle: 'Select all that apply',
  options: primaryReasonOptions
};

const primaryReasonOtherQuestion = {
  id: 'primary-reason-other',
  type: 'textarea',
  question: 'Please tell us more about your reason for requesting this medication',
  placeholder: 'Describe your specific goals...'
};

const currentSymptomsQuestion = {
  id: 'current-symptoms',
  type: 'multi-select',
  question: 'Are you currently experiencing any of the following?',
  subtitle: 'Select all that apply',
  options: currentSymptomsOptions
};

const currentSymptomsOtherQuestion = {
  id: 'current-symptoms-other',
  type: 'textarea',
  question: 'Please provide further information about this condition',
  placeholder: 'Describe your symptoms in more detail...'
};

const previousTreatmentQuestion = {
  id: 'previous-treatment',
  type: 'single-select',
  question: 'Have you ever received anti-aging treatment before with metformin, MIC, vitamin B12, glutathione or NAD?',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' }
  ]
};

const previousReactionQuestion = {
  id: 'previous-reaction',
  type: 'single-select',
  question: 'Did you experience any allergies or negative reactions to any of these?',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' }
  ]
};

const previousReactionDetailsQuestion = {
  id: 'previous-reaction-details',
  type: 'textarea',
  question: 'Please tell us more about what you used, when this was used, and what side effect you experienced',
  placeholder: 'e.g., I used NAD+ injections 6 months ago and experienced nausea and headaches...'
};

const medicalHistoryQuestion = {
  id: 'medical-history',
  type: 'single-select',
  question: 'Do you have a known history of glucose-6-phosphate dehydrogenase (G6PD) deficiency, chronic kidney disease (CKD) or liver cirrhosis?',
  options: [
    { label: 'Yes', value: 'yes', disqualifies: true },
    { label: 'No', value: 'no' }
  ]
};

const cancerTreatmentQuestion = {
  id: 'cancer-treatment',
  type: 'single-select',
  question: 'Are you currently undergoing treatment for cancer, including chemotherapy or radiation therapy?',
  options: [
    { label: 'Yes', value: 'yes', disqualifies: true },
    { label: 'No', value: 'no' }
  ]
};

const additionalQuestionsQuestion = {
  id: 'additional-questions',
  type: 'textarea',
  question: 'What other information or questions do you have for the doctor?',
  subtitle: 'Feel free to share any concerns or questions',
  placeholder: 'Type your questions or concerns here...'
};

const basicContactQuestion = {
  id: 'basic-contact',
  type: 'basic-contact',
  question: 'How can we reach you?',
  subtitle: "We'll use this to send updates about your treatment"
};

const mainConsentQuestion = {
  id: 'main-consent',
  type: 'consent-dropdown',
  question: 'Combined Informed Consent',
  consentText: `You are requesting treatment with one or more lifestyle-enhancement therapies including MIC injections, Glutathione, and/or NAD+ (Nicotinamide Adenine Dinucleotide) to support metabolism, enhance energy production, promote cellular health, and potentially provide anti-aging benefits. These therapies are not intended for treatment of any specific medical condition.

PURPOSE OF THIS CONSENT
This form ensures you understand:
• The reason for prescribing these products
• Their potential benefits and risks
• The off-label status of certain components
• The monitoring and follow-up plan
• Your responsibilities during treatment

BACKGROUND INFORMATION

MIC+B12: Combines lipotropic agents (Methionine, Inositol, Choline) with Vitamin B12 to aid fat metabolism and boost energy.

Vitamin B12 (cobalamin): Is essential for red blood cell production, neurological function, and DNA synthesis. Deficiency can arise from dietary insufficiency (e.g., vegan diets), malabsorption, or interactions with medications such as Metformin or proton-pump inhibitors.

Glutathione: Is a natural antioxidant that reduces oxidative stress, supports detoxification, and enhances immune function.

NAD+: Is a coenzyme vital for mitochondrial energy production, DNA repair, and overall cellular health.

Metformin: Is a prescription medication for type 2 diabetes that is being studied off-label for metabolic and anti-aging effects.

OFF-LABEL USE NOTICE
Glutathione, NAD+, and Metformin are not FDA-approved for wellness or anti-aging indications. Their use in this program is off-label, intended solely for health optimization and lifestyle enhancement.

POTENTIAL BENEFITS
• MIC+B12: Enhanced metabolism and fat burning
• Vitamin B12: Improved energy, neurological health, and cognitive function
• NAD+: Increased cellular energy and mitochondrial function
• Glutathione: Detoxification and antioxidant protection
• Metformin: Improved insulin sensitivity and metabolic efficiency
• All therapies: Increased vitality, reduced fatigue, and potential anti-aging effects

POTENTIAL RISKS AND SIDE EFFECTS

MIC+B12:
• Common: Injection-site redness, swelling, pain; nausea; mild diarrhea
• Serious: Allergic reaction (rash, itching, breathing difficulty); elevated liver enzymes; rare injection-site infection

Vitamin B12:
• Common: Mild diarrhea; skin rash or itching; headache; nausea
• Rare: Severe allergic reactions (anaphylaxis); hypokalemia with rapid correction; exacerbation of acne or rosacea

Glutathione:
• Common: GI discomfort; headache; skin reactions (rash, itching)
• Rare: Allergic reaction; localized skin lightening

NAD+:
• Common: Nausea; fatigue; flushing; headache; abdominal cramping; dizziness
• Less common: Anxiety; sweating; muscle cramps; injection-site pain or swelling

Metformin:
• Common: GI upset (nausea, bloating, diarrhea)
• Rare but serious: Lactic acidosis (especially with kidney or liver impairment); long-term vitamin B12 deficiency

RISKS AND CONSIDERATIONS
• Allergic Reactions: Discontinue treatment and seek immediate care if you experience rash, swelling, or breathing difficulty.
• Kidney Function: Use Metformin cautiously with impaired kidney function.
• Supplement Interactions: Disclose all medications and supplements to avoid interactions, particularly those affecting absorption of Vitamin B12.

MONITORING AND FOLLOW-UP
• Attend all scheduled follow-up visits for assessment of therapy response and side-effect monitoring.

PATIENT RESPONSIBILITIES
• Follow the prescribed treatment plan exactly
• Report new or worsening symptoms promptly
• Avoid altering dosage or frequency without medical approval
• Attend all recommended follow-up appointments
• Inform your provider of any changes in medications, supplements, or health status

By selecting below, you confirm that you:
• Have read and understood the information provided
• Understand the potential risks and benefits of using these products
• Understand the off-label nature of this treatment
• Agree to adhere to the monitoring plan and inform your healthcare provider of any side effects or concerns`,
  options: [
    { label: 'I have read the above information, I understand the risks and would like to proceed', value: 'consent' },
    { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true }
  ]
};

const truthfulnessConsentQuestion = {
  id: 'truthfulness-consent',
  type: 'consent-dropdown',
  question: 'Consent (Truthfulness)',
  consentText: `Please attest to the following confirming that all information you have provided to us is true and complete.

I verify that I am the patient and that I have answered the questions asked in this intake form. I confirm that I have reviewed and understood all the questions asked of me. I attest that the answers and information I have provided in this questionnaire is true and complete to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor. I will not hold the doctor or affiliated medical practice responsible for any oversights or omissions, whether intentional or not, in the information that I provided.`,
  checkbox: {
    text: 'I accept the Beluga Health Privacy Policy, give my Consent, and agree to the Terms of Service.',
    links: [
      { text: 'Privacy Policy', url: 'https://customerconsents.s3.amazonaws.com/Beluga_Health_PA_Privacy_Policy.pdf' },
      { text: 'Consent', url: 'https://customerconsents.s3.amazonaws.com/Beluga_Health_Telemedicine_Informed_Consent.pdf' },
      { text: 'Terms of Service', url: 'https://fitpal.com/terms-and-conditions/' }
    ]
  },
  options: [
    { label: 'I have read the above information and I do consent and wish to move forward', value: 'consent' },
    { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true }
  ]
};

const identityVerificationStep = {
  id: 'identity-verification',
  type: 'file-upload',
  question: 'Identity Verification',
  subtitle: `Upload a photo of a valid government-issued photo ID such as a driver's license or passport which has your picture, name and date of birth clearly visible.

If applicable, upload a photo of the front and the back especially when using a military ID as your date of birth is on the back of the card.

Make sure the photo is clear and legible.`,
  // Required by Beluga — ID photo cannot be skipped (2026-06-27).
};

const shippingAddressStep = {
  id: 'shipping-address',
  type: 'shipping-address',
  question: 'Shipping Address'
};

const checkoutStep = {
  id: 'checkout',
  type: 'checkout',
  question: 'Select Your Treatment Plan'
};

const completeStep = {
  id: 'complete',
  type: 'complete',
  question: 'Complete'
};

// ============================================
// ANTI-AGING FLOW CONFIGURATION
// ============================================
export const antiAgingQuestions = {
  sexAtBirthQuestion,
  pregnancyStatusQuestion,
  pregnancyConsentQuestion,
  heightWeightQuestion,
  medicalConditionsQuestion,
  medicationsListQuestion,
  allergiesQuestion,
  primaryReasonQuestion,
  primaryReasonOtherQuestion,
  currentSymptomsQuestion,
  currentSymptomsOtherQuestion,
  previousTreatmentQuestion,
  previousReactionQuestion,
  previousReactionDetailsQuestion,
  medicalHistoryQuestion,
  cancerTreatmentQuestion,
  additionalQuestionsQuestion,
  basicContactQuestion,
  mainConsentQuestion,
  truthfulnessConsentQuestion,
  identityVerificationStep,
  shippingAddressStep,
  checkoutStep,
  completeStep
};

// Main flow array
export const antiAgingFlow = [
  sexAtBirthQuestion,
  pregnancyStatusQuestion,
  pregnancyConsentQuestion,
  heightWeightQuestion,
  medicalConditionsQuestion,
  medicationsListQuestion,
  allergiesQuestion,
  primaryReasonQuestion,
  primaryReasonOtherQuestion,
  currentSymptomsQuestion,
  currentSymptomsOtherQuestion,
  previousTreatmentQuestion,
  previousReactionQuestion,
  previousReactionDetailsQuestion,
  medicalHistoryQuestion,
  cancerTreatmentQuestion,
  additionalQuestionsQuestion,
  basicContactQuestion,
  mainConsentQuestion,
  truthfulnessConsentQuestion,
  identityVerificationStep,
  shippingAddressStep,
  checkoutStep,
  completeStep
];

// Get question by ID
export const getAntiAgingQuestionById = (id) => {
  return antiAgingFlow.find(q => q.id === id);
};

// Build dynamic flow based on answers (handles conditional logic)
export const buildDynamicAntiAgingFlow = (answers) => {
  let flow = [...antiAgingFlow];
  
  // Handle conditional questions based on sex
  const sexAtBirth = answers['sex-at-birth'];
  
  if (sexAtBirth === 'male') {
    // Remove pregnancy-related questions for males
    flow = flow.filter(q => !['pregnancy-status', 'pregnancy-consent'].includes(q.id));
  }
  
  // Handle pregnancy consent - only show if female AND pregnancy-status is 'no'
  if (sexAtBirth === 'female') {
    const pregnancyStatus = answers['pregnancy-status'];
    if (pregnancyStatus !== 'no') {
      flow = flow.filter(q => q.id !== 'pregnancy-consent');
    }
  }
  
  // Handle primary reason 'other' - only show if 'other' is selected
  const primaryReason = answers['primary-reason'] || [];
  if (!Array.isArray(primaryReason) || !primaryReason.includes('other')) {
    flow = flow.filter(q => q.id !== 'primary-reason-other');
  }
  
  // Handle current symptoms 'other' - only show if 'other' is selected
  const currentSymptoms = answers['current-symptoms'] || [];
  if (!Array.isArray(currentSymptoms) || !currentSymptoms.includes('other')) {
    flow = flow.filter(q => q.id !== 'current-symptoms-other');
  }
  
  // Handle previous treatment follow-ups
  const previousTreatment = answers['previous-treatment'];
  if (previousTreatment !== 'yes') {
    flow = flow.filter(q => !['previous-reaction', 'previous-reaction-details'].includes(q.id));
  }
  
  // Handle previous reaction details - only show if had reaction
  const previousReaction = answers['previous-reaction'];
  if (previousReaction !== 'yes') {
    flow = flow.filter(q => q.id !== 'previous-reaction-details');
  }
  
  return flow;
};

// Check if answer is disqualifying
export const isDisqualifyingAnswer = (questionId, answer) => {
  if (questionId === 'pregnancy-status' && answer === 'yes') {
    return {
      disqualified: true,
      reason: 'These treatments are not recommended during pregnancy or while breastfeeding. Please consult with your healthcare provider for alternative options.'
    };
  }
  
  if (questionId === 'medical-history' && answer === 'yes') {
    return {
      disqualified: true,
      reason: 'Due to your medical history (G6PD deficiency, chronic kidney disease, or liver cirrhosis), you may not be a candidate for these anti-aging treatments. Please consult with your healthcare provider.'
    };
  }
  
  if (questionId === 'cancer-treatment' && answer === 'yes') {
    return {
      disqualified: true,
      reason: 'These treatments are not recommended for patients currently undergoing cancer treatment including chemotherapy or radiation therapy. Please consult with your oncologist.'
    };
  }
  
  const consentQuestions = ['pregnancy-consent', 'main-consent', 'truthfulness-consent'];
  if (consentQuestions.includes(questionId) && answer === 'decline') {
    return {
      disqualified: true,
      reason: 'You have indicated that you do not wish to continue with this treatment.'
    };
  }
  
  return { disqualified: false };
};

// Anti-Aging treatment plans
export const antiAgingPlans = [
  {
    category: 'NAD+',
    description: 'Cellular energy and anti-aging support',
    plans: [
      {
        plan_slug: 'nad-sc-monthly',
        plan_name: 'NAD+ Subcutaneous - Monthly',
        plan_price: 249,
        plan_content: 'NAD+ 100mg/mL vial,Subcutaneous injection kit,Monthly supply,Provider consultation,Free shipping'
      },
      {
        plan_slug: 'nad-sc-quarterly',
        plan_name: 'NAD+ Subcutaneous - Quarterly',
        plan_price: 649,
        plan_content: 'Best Value - Save $98,3x NAD+ 100mg/mL vials,Subcutaneous injection kit,3-month supply,Provider consultation,Free shipping,Priority support'
      },
      {
        plan_slug: 'nad-injection-monthly',
        plan_name: 'NAD+ Injection - Monthly',
        plan_price: 299,
        plan_content: 'NAD+ 200mg/mL vial,Intramuscular injection kit,Monthly supply,Provider consultation,Free shipping'
      }
    ]
  },
  {
    category: 'MIC + B12',
    description: 'Metabolism and energy boost',
    plans: [
      {
        plan_slug: 'mic-b12-monthly',
        plan_name: 'MIC + B12 Injections - Monthly',
        plan_price: 129,
        plan_content: 'MIC/B12 injection vial,Lipotropic fat metabolism support,Monthly supply,Provider consultation,Free shipping'
      },
      {
        plan_slug: 'mic-b12-quarterly',
        plan_name: 'MIC + B12 Injections - Quarterly',
        plan_price: 329,
        plan_content: 'Best Value - Save $58,3x MIC/B12 injection vials,3-month supply,Provider consultation,Free shipping'
      }
    ]
  },
  {
    category: 'Vitamin B12',
    description: 'Energy and neurological support',
    plans: [
      {
        plan_slug: 'b12-monthly',
        plan_name: 'Vitamin B12 Injections - Monthly',
        plan_price: 79,
        plan_content: 'B12 1000mcg injection vial,Monthly supply,Provider consultation,Free shipping'
      }
    ]
  },
  {
    category: 'Glutathione',
    description: 'Antioxidant and detoxification',
    plans: [
      {
        plan_slug: 'glutathione-monthly',
        plan_name: 'Glutathione Injections - Monthly',
        plan_price: 149,
        plan_content: 'Glutathione 200mg/mL vial,Powerful antioxidant,Monthly supply,Provider consultation,Free shipping'
      },
      {
        plan_slug: 'glutathione-quarterly',
        plan_name: 'Glutathione Injections - Quarterly',
        plan_price: 399,
        plan_content: 'Best Value - Save $48,3x Glutathione vials,3-month supply,Provider consultation,Free shipping'
      }
    ]
  },
];

// Bundle options
export const antiAgingBundles = [
  {
    plan_slug: 'anti-aging-starter',
    plan_name: 'Anti-Aging Starter Bundle',
    plan_price: 399,
    plan_content: 'NAD+ SC Monthly,MIC+B12 Monthly,Glutathione Monthly,Save $128,Provider consultation,Free shipping',
    includes: ['nad-sc-monthly', 'mic-b12-monthly', 'glutathione-monthly']
  }
];

export default antiAgingQuestions;
